import { ShieldCheckIcon } from "@heroicons/react/24/outline";
import { useVerification } from "src/contexts/VerificationContext";
import { useEffect, useState, useCallback, useMemo } from "react";
import BlurredCard from "../BlurredCard";
import { Immunization } from "src/shared/types";
import Loading from "../Loading";
import { capitalizeStatus } from "src/utils/utils";
import { apiGet } from "src/utils/api";
import { StaticDataTable, StaticDataColumnProps, StaticDataRow } from "../table/StaticDataTable";
import { SortDirection } from "../table/useTableData";
import { useCurrentOrganization, useOrganizationContext } from "src/contexts/OrganizationContext";
import TableBadge from "../table/TableBadge";
import TableDateTime, { getDateTimeSortValue } from "../table/TableDateTime";

const statusColorMap: Record<string, string> = {
  completed: "bg-emerald-500",
  "entered-in-error": "bg-rose-500",
  "not-done": "bg-gray-500",
};

const immunizationColumns: StaticDataColumnProps[] = [
  { title: "Name", sortable: true },
  { title: "Date & Time", sortable: true, defaultSort: true, defaultSortDirection: SortDirection.DESC },
  { title: "Status", sortable: true },
  { title: "Lot Number", sortable: true },
  { title: "Manufacturer", sortable: true },
  { title: "Reason", sortable: false },
];

const convertImmunizationsToRows = (immunizations: Immunization[]): StaticDataRow[][] => {
  return immunizations.map((immunization) => [
    {
      name: "Name",
      value: immunization.name,
      element: <span className="text-gray-900">{immunization.name}</span>
    },
    {
      name: "Date & Time",
      value: getDateTimeSortValue(immunization.date, immunization.time),
      element: (
        <TableDateTime
          date={immunization.date}
          startTime={immunization.time}
        />
      )
    },
    {
      name: "Status",
      value: capitalizeStatus(immunization.status),
      element:  (
        <TableBadge
          text={capitalizeStatus(immunization.status)}
          bgColorClass={statusColorMap[immunization.status] ?? "bg-sky-500"}
        />
      )
    },
    {
      name: "Lot Number",
      value: immunization.lotNumber || "—",
      element: <span className="text-gray-600 font-mono text-sm">{immunization.lotNumber || "—"}</span>
    },
    {
      name: "Manufacturer",
      value: immunization.manufacturer || "—",
      element: <span className="text-gray-700">{immunization.manufacturer || "—"}</span>
    },
    {
      name: "Reason",
      value: immunization.reasonCode || "—",
      element: <span className="text-gray-600 text-sm">{immunization.reasonCode || "—"}</span>
    },
  ]);
};

const dummyImmunizations: Immunization[] = [
  {
    name: "Immunization 1",
    date: new Date().toISOString().split('T')[0],
    time: "10:00",
    status: "completed",
    lotNumber: "1234567890",
    manufacturer: "Manufacturer 1",
    reasonCode: "Reason 1",
  },
  {
    name: "Immunization 2",
    date: new Date().toISOString().split('T')[0],
    time: "10:00",
    status: "entered-in-error",
    lotNumber: "1234567890",
    manufacturer: "Manufacturer 2",
    reasonCode: "Reason 2",
  },
];

type ImmunizationsCardProps = {
  yearFrom: string;
  yearTo: string;
  refreshKey?: number;
};

const ImmunizationsCard = ({ yearFrom, yearTo, refreshKey }: ImmunizationsCardProps) => {
  const organization = useCurrentOrganization();
  const { patientId } = useOrganizationContext();
  const [ immunizations, setImmunizations ] = useState<Immunization[]>([]);
  const [ loading, setLoading ] = useState(false);
  const { isVerified } = useVerification();
  const showLoading = isVerified && loading;

  const fetchImmunizations = useCallback(async () => {
    if (!organization || !patientId) {
      setImmunizations([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const params = { yearFrom, yearTo };
      const response = await apiGet(`/api/${organization.id}/patients/${patientId}/immunizations`, params);
      const data = await response.json();

      if (!data.error) {
        setImmunizations(data.immunizations);
      } else {
        setImmunizations([]);
        window.location.href = "/error";
      }
    } catch (_) {
      setImmunizations([]);
    } finally {
      setLoading(false);
    }
  }, [ organization, patientId, yearFrom, yearTo ]);

  useEffect(() => {
    if (isVerified) {
      fetchImmunizations();
    } else {
      setImmunizations(dummyImmunizations);
    }
  }, [ isVerified, fetchImmunizations, yearFrom, yearTo, refreshKey ]);

  const tableRows = useMemo(() =>
    convertImmunizationsToRows(immunizations),
    [ immunizations ]
  );

  return (
    <BlurredCard>
      {showLoading ? (
        <div className="flex justify-center mt-8">
          <Loading message="Loading Immunizations..." borderColor="border-brand-500" />
        </div>
      ) : (
        <div className="-mx-2">
          {immunizations.length > 0 ? (
            <StaticDataTable
              tableId="immunizations-table"
              columns={immunizationColumns}
              rows={tableRows}
              updateUrlParams={false}
              rowId={(item) => `${item.Name}-${item["Date & Time"]}`}
            />
          ) : (
            <div className="text-center py-12 text-gray-400">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <ShieldCheckIcon className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm font-medium">No immunizations found</p>
            </div>
          )}
        </div>
      )}
    </BlurredCard>
  );
};

export default ImmunizationsCard;
